/**
 * Docuforge — Intelligent Document Processing UI (PRODUCT_SPEC §4.95).
 *
 * Three tabs:
 *  1. Doc Types — list / enable / disable / configure document type templates.
 *  2. Documents — paginated list of recently processed documents, with detail
 *     view and a Re-classify / Re-extract / Discard action set.
 *  3. Review Queue — documents in `review_required` state, with a two-pane
 *     verification UI (PDF on the left, editable field grid on the right).
 *
 * The page reuses the existing `/api/docuforge/...` REST surface — no new
 * frontend abstractions are introduced. Auth follows the project pattern
 * (Bearer token from localStorage).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL, WS_BASE_URL } from './config';

function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

function describeFetchError(e) {
  if (e && typeof e === 'object' && 'status' in e && typeof e.status === 'number') {
    const reason = e.status === 401 ? 'authentication required'
      : e.status === 403 ? 'forbidden'
      : e.status === 404 ? 'not found'
      : (e.statusText || '');
    return `HTTP ${e.status}${reason ? ` ${reason}` : ''}`;
  }
  return e && e.message ? e.message : String(e);
}

function confidencePill(conf) {
  const c = Number(conf || 0);
  let bg = 'var(--accent-color)';
  let label = `${Math.round(c * 100)}%`;
  if (c < 0.7) bg = '#c0392b';
  else if (c < 0.85) bg = '#d68910';
  else bg = '#1e8449';
  return (
    <span style={{
      display: 'inline-block',
      background: bg,
      color: '#fff',
      borderRadius: 999,
      padding: '2px 8px',
      fontSize: 11,
      fontWeight: 600,
      minWidth: 38,
      textAlign: 'center',
    }}>{label}</span>
  );
}

function statusBadge(status) {
  const colors = {
    queued: '#7f8c8d',
    preprocessing: '#5dade2',
    classifying: '#5dade2',
    extracting: '#5dade2',
    review_required: '#d68910',
    extracted: '#1e8449',
    failed: '#c0392b',
    discarded: '#7f8c8d',
  };
  return (
    <span style={{
      background: colors[status] || '#7f8c8d',
      color: '#fff',
      borderRadius: 4,
      padding: '2px 8px',
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    }}>{status}</span>
  );
}

export default function Docuforge({ workspaceId, onBack }) {
  const [tab, setTab] = useState('documents');
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/docuforge/status`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 20px', borderBottom: '1px solid var(--border-color)',
        background: 'var(--bg-secondary)',
      }}>
        <button onClick={onBack} style={btnGhost}>← Back</button>
        <div style={{ fontSize: 20, fontWeight: 700 }}>📥 docuforge</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          Intelligent Document Processing
        </div>
        <div style={{ flex: 1 }} />
        {status && (
          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            plan: <b>{status.license_plan}</b> · storage: <b>{status.storage_backend}</b>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, padding: '8px 20px', borderBottom: '1px solid var(--border-color)' }}>
        {[
          { id: 'documents', label: 'Documents' },
          { id: 'review', label: 'Review Queue' },
          { id: 'doc_types', label: 'Doc Types' },
          { id: 'sources', label: 'Sources' },
          { id: 'reports', label: 'Reports' },
          { id: 'settings', label: 'Settings' },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={tabPillStyle(tab === t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ padding: 12, background: '#3a1d1d', color: '#ffb4b4', borderBottom: '1px solid var(--border-color)' }}>
          {String(error)}
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {tab === 'documents' && <DocumentsTab workspaceId={workspaceId} setError={setError} />}
        {tab === 'review' && <ReviewQueueTab workspaceId={workspaceId} setError={setError} />}
        {tab === 'doc_types' && <DocTypesTab workspaceId={workspaceId} setError={setError} />}
        {tab === 'sources' && <SourcesTab workspaceId={workspaceId} setError={setError} />}
        {tab === 'reports' && <ReportsTab workspaceId={workspaceId} setError={setError} licenseAllowed={!!(status && status.features && status.features['docuforge.accuracy_dashboard'] && status.features['docuforge.accuracy_dashboard'].allowed)} />}
        {tab === 'settings' && <SettingsTab workspaceId={workspaceId} setError={setError} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Doc Types tab
// ---------------------------------------------------------------------------

const ENGINE_OPTIONS = [
  { value: 'llm_vision', label: 'LLM Vision (OpenAI / Claude / Gemini)' },
  { value: 'tesseract', label: 'Tesseract (free, on-prem)' },
  { value: 'textract', label: 'AWS Textract' },
  { value: 'azure_document_intelligence', label: 'Azure Document Intelligence' },
  { value: 'google_document_ai', label: 'Google Document AI' },
  { value: 'mistral_ocr', label: 'Mistral OCR' },
];

function DocTypesTab({ workspaceId, setError }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/doc-types`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((data) => setTypes(data.doc_types || []))
      .catch((e) => setError(`Failed to load doc types: ${e.statusText || e.message || e}`))
      .finally(() => setLoading(false));
  }, [workspaceId, setError]);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = (dt) => {
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/doc-types/${dt.id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_enabled: !dt.is_enabled }),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(load)
      .catch((e) => setError(`Update failed: ${e.statusText || e.message || e}`));
  };

  if (loading) return <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>;
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(true)} style={btnPrimary}>+ New Doc Type</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {types.map((dt) => (
          <div key={dt.id} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{dt.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>· {dt.key}</div>
              <div style={{ flex: 1 }} />
              <span style={{
                background: dt.family === 'prebuilt' ? '#5d4eaa' : '#444',
                color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase',
              }}>{dt.family}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
              Engine: {dt.engine_default} · Threshold {Math.round((dt.confidence_threshold || 0) * 100)}%
              {' '}· v{dt.version}
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
              <button onClick={() => toggleEnabled(dt)} style={dt.is_enabled ? btnSuccess : btnGhost}>
                {dt.is_enabled ? '✓ Enabled' : 'Disabled'}
              </button>
              <button onClick={() => setEditing(dt)} style={btnGhost}>⚙ Configure</button>
            </div>
          </div>
        ))}
        {!types.length && (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            No doc types yet. Visit this page to seed the prebuilt catalog automatically.
          </div>
        )}
      </div>
      {editing && (
        <DocTypeConfigModal
          workspaceId={workspaceId}
          docType={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          setError={setError}
        />
      )}
      {creating && (
        <NewDocTypeWizard
          workspaceId={workspaceId}
          existingKeys={types.map((t) => t.key)}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
          setError={setError}
        />
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// 3-step Custom Doc Type Wizard (P4)
// ---------------------------------------------------------------------------

const FIELD_TYPES = ['string', 'number', 'date', 'currency', 'email', 'address', 'boolean'];
const NORMALIZERS = ['', 'date', 'currency', 'email', 'number', 'phone'];

function NewDocTypeWizard({ workspaceId, existingKeys, onClose, onCreated, setError }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [family, setFamily] = useState('one_off');
  const [fields, setFields] = useState([
    { name: 'vendor_name', label: 'Vendor Name', type: 'string', normalizer: '', required: true },
  ]);
  const [engine, setEngine] = useState('llm_vision');
  const [threshold, setThreshold] = useState(80);
  const [testFile, setTestFile] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createdId, setCreatedId] = useState(null);

  const keyValid = key && /^[a-z][a-z0-9_]{0,62}$/.test(key) && !existingKeys.includes(key);
  const fieldsValid = fields.length > 0 && fields.every((f) => f.name && /^[a-z][a-z0-9_]*$/.test(f.name));

  const autoKey = (n) => n.toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);

  const buildSchema = () => {
    const properties = {};
    const required = [];
    for (const f of fields) {
      properties[f.name] = { type: f.type, label: f.label || f.name };
      if (f.normalizer) properties[f.name].normalizer = f.normalizer;
      if (f.required) required.push(f.name);
    }
    return { type: 'object', properties, required };
  };

  const addField = () => setFields([...fields, { name: '', label: '', type: 'string', normalizer: '', required: false }]);
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const updateField = (i, patch) => setFields(fields.map((f, idx) => idx === i ? { ...f, ...patch } : f));

  const runTest = () => {
    if (!testFile) return;
    if (!createdId) {
      setError('Save the doc type first (Step 3) before running a test extraction.');
      return;
    }
    setTesting(true);
    setTestResult(null);
    const fd = new FormData();
    fd.append('file', testFile);
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/doc-types/${createdId}/test`, {
      method: 'POST',
      headers: authHeaders(),
      body: fd,
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setTestResult)
      .catch((e) => setError(`Test failed: ${e.statusText || e.message || e}`))
      .finally(() => setTesting(false));
  };

  const save = () => {
    setSaving(true);
    const body = {
      key,
      name,
      family,
      field_schema: buildSchema(),
      classifier_json: { rules: [], fewshot_examples: [] },
      extractor_json: {},
      engine_default: engine,
      confidence_threshold: Math.min(0.99, Math.max(0.5, threshold / 100)),
      is_enabled: true,
    };
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/doc-types`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((dt) => setCreatedId(dt.id))
      .catch((e) => setError(`Create failed: ${e.statusText || e.message || e}`))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
        borderRadius: 8, padding: 20, width: 600, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>New Doc Type · Step {step} of 3</div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={btnGhost}>×</button>
        </div>
        <div style={{ display: 'flex', gap: 4, marginTop: 10, marginBottom: 14 }}>
          {[1, 2, 3].map((s) => (
            <div key={s} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: s <= step ? '#5d4eaa' : 'var(--border-color)',
            }} />
          ))}
        </div>

        {step === 1 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Identify the doc type</div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Display Name</div>
              <input value={name} onChange={(e) => { setName(e.target.value); if (!key) setKey(autoKey(e.target.value)); }}
                     placeholder="e.g. Acme Purchase Order"
                     style={{ ...inputStyle, width: '100%' }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Key (lowercase, underscores)
                {key && !keyValid && <span style={{ color: '#e57373', marginLeft: 8 }}>· invalid or in use</span>}
              </div>
              <input value={key} onChange={(e) => setKey(e.target.value)}
                     placeholder="acme_purchase_order"
                     style={{ ...inputStyle, width: '100%', fontFamily: 'monospace' }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Family</div>
              <select value={family} onChange={(e) => setFamily(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                <option value="one_off">One-off (single template)</option>
                <option value="recurring">Recurring template (same vendor, repeats)</option>
                <option value="freeform">Freeform (varying layouts)</option>
              </select>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                Hint: choose a prebuilt engine (Textract / Azure DI) for recurring; LLM Vision for freeform.
              </div>
            </div>
            <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={btnGhost}>Cancel</button>
              <button disabled={!name || !keyValid} onClick={() => setStep(2)} style={btnPrimary}>Next →</button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Define extracted fields</div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 10 }}>
              Add the fields docuforge should pull out of every document. You can add more later.
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              {fields.map((f, i) => (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '1.2fr 1.4fr 1fr 1fr auto auto',
                  gap: 6, alignItems: 'center',
                }}>
                  <input value={f.name} onChange={(e) => updateField(i, { name: e.target.value })}
                         placeholder="field_name" style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 11 }} />
                  <input value={f.label} onChange={(e) => updateField(i, { label: e.target.value })}
                         placeholder="Display label" style={{ ...inputStyle, fontSize: 11 }} />
                  <select value={f.type} onChange={(e) => updateField(i, { type: e.target.value })}
                          style={{ ...inputStyle, fontSize: 11 }}>
                    {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <select value={f.normalizer} onChange={(e) => updateField(i, { normalizer: e.target.value })}
                          style={{ ...inputStyle, fontSize: 11 }}>
                    {NORMALIZERS.map((n) => <option key={n} value={n}>{n || '(none)'}</option>)}
                  </select>
                  <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    <input type="checkbox" checked={f.required} onChange={(e) => updateField(i, { required: e.target.checked })} />
                    req
                  </label>
                  <button onClick={() => removeField(i)} style={btnGhost} title="Remove">×</button>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8 }}>
              <button onClick={addField} style={btnGhost}>+ Add field</button>
            </div>
            <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'space-between' }}>
              <button onClick={() => setStep(1)} style={btnGhost}>← Back</button>
              <button disabled={!fieldsValid} onClick={() => setStep(3)} style={btnPrimary}>Next →</button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Choose engine + test against a sample</div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Default Engine</div>
              <select value={engine} onChange={(e) => setEngine(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                {ENGINE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Confidence Threshold: {threshold}%</div>
              <input type="range" min={50} max={99} value={threshold}
                     onChange={(e) => setThreshold(Number(e.target.value))} style={{ width: '100%' }} />
            </div>

            <div style={{
              marginTop: 14, padding: 10, border: '1px solid var(--border-color)', borderRadius: 6,
              background: 'var(--bg-primary)',
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                {createdId ? 'Test extraction (no persist)' : 'Save first, then test'}
              </div>
              {!createdId && (
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                  Click "Save Doc Type" below — once created, you can upload a sample to verify the schema before enabling.
                </div>
              )}
              {createdId && (
                <>
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.txt"
                         onChange={(e) => setTestFile(e.target.files?.[0] || null)}
                         style={{ fontSize: 11 }} />
                  <button disabled={!testFile || testing} onClick={runTest} style={{ ...btnGhost, marginLeft: 8 }}>
                    {testing ? 'Testing…' : 'Run test'}
                  </button>
                  {testResult && (
                    <div style={{ marginTop: 8, fontSize: 11 }}>
                      <div style={{ color: 'var(--text-secondary)' }}>
                        Engine: {testResult.engine_used} · Pages: {testResult.page_count} · ~${(testResult.cost_usd_estimate || 0).toFixed(4)}
                      </div>
                      <pre style={{
                        marginTop: 6, padding: 8, background: 'var(--bg-secondary)', borderRadius: 4,
                        maxHeight: 200, overflow: 'auto', fontSize: 10,
                      }}>{JSON.stringify(testResult.fields, null, 2)}</pre>
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'space-between' }}>
              <button onClick={() => setStep(2)} style={btnGhost}>← Back</button>
              <div style={{ display: 'flex', gap: 8 }}>
                {!createdId && (
                  <button disabled={saving} onClick={save} style={btnPrimary}>
                    {saving ? 'Saving…' : 'Save Doc Type'}
                  </button>
                )}
                {createdId && (
                  <button onClick={onCreated} style={btnSuccess}>✓ Finish</button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function DocTypeConfigModal({ workspaceId, docType, onClose, onSaved, setError }) {
  const [engine, setEngine] = useState(docType.engine_default || 'llm_vision');
  const [threshold, setThreshold] = useState(Math.round((docType.confidence_threshold || 0.85) * 100));
  const [qcPercentage, setQcPercentage] = useState(Math.max(0, Math.min(100, Number(docType.qc_percentage || 0))));
  const [extractor, setExtractor] = useState(docType.extractor_json || {});
  const [credentials, setCredentials] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/credentials/`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setCredentials(Array.isArray(data) ? data : (data.credentials || [])))
      .catch(() => setCredentials([]));
  }, []);

  const updateExtractor = (patch) => setExtractor({ ...(extractor || {}), ...patch });

  const save = () => {
    setSaving(true);
    const body = {
      engine_default: engine,
      confidence_threshold: Math.min(0.99, Math.max(0.5, threshold / 100)),
      qc_percentage: Math.max(0, Math.min(100, Number(qcPercentage) || 0)),
      extractor_json: extractor,
    };
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/doc-types/${docType.id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(onSaved)
      .catch((e) => setError(`Save failed: ${e.statusText || e.message || e}`))
      .finally(() => setSaving(false));
  };

  const credentialPicker = (label, hint) => (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</div>
      <select
        value={extractor.credential_id || ''}
        onChange={(e) => updateExtractor({ credential_id: e.target.value || null })}
        style={{ ...inputStyle, width: '100%' }}
      >
        <option value="">— None (will fall back to LLM Vision) —</option>
        {credentials.map((c) => (
          <option key={c.id} value={c.id}>{c.name || c.id}{c.type ? ` (${c.type})` : ''}</option>
        ))}
      </select>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{hint}</div>}
    </div>
  );

  const engineConfig = () => {
    switch (engine) {
      case 'llm_vision':
        return (
          <>
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>LLM Provider</div>
              <select value={extractor.provider || 'openai'} onChange={(e) => updateExtractor({ provider: e.target.value })}
                      style={{ ...inputStyle, width: '100%' }}>
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic (Claude)</option>
                <option value="gemini">Google Gemini</option>
              </select>
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Model (optional)</div>
              <input value={extractor.model || ''} onChange={(e) => updateExtractor({ model: e.target.value })}
                     placeholder="defaults: gpt-4o-mini / claude-haiku-4-5-20251001 / gemini-2.5-flash"
                     style={{ ...inputStyle, width: '100%' }} />
            </div>
            {credentialPicker('Provider Credential', 'Without a credential the extractor falls back to a deterministic regex heuristic (every doc → review).')}
          </>
        );
      case 'tesseract':
        return (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
            Tesseract runs on-prem with no credentials. Requires <code>pytesseract</code> + <code>pdf2image</code> on the server. Falls back to LLM Vision if missing.
          </div>
        );
      case 'textract':
        return credentialPicker('AWS Credential (Textract permission required)', 'Falls back to LLM Vision when no credential is attached.');
      case 'azure_document_intelligence':
        return (
          <>
            {credentialPicker('Azure DI Credential (endpoint + api_key)')}
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Azure Model</div>
              <select value={extractor.azure_model || 'prebuilt-document'} onChange={(e) => updateExtractor({ azure_model: e.target.value })}
                      style={{ ...inputStyle, width: '100%' }}>
                <option value="prebuilt-document">prebuilt-document (general)</option>
                <option value="prebuilt-invoice">prebuilt-invoice</option>
                <option value="prebuilt-receipt">prebuilt-receipt</option>
                <option value="prebuilt-idDocument">prebuilt-idDocument</option>
                <option value="prebuilt-tax.us.w2">prebuilt-tax.us.w2</option>
                <option value="prebuilt-tax.us.1099">prebuilt-tax.us.1099</option>
                <option value="prebuilt-businessCard">prebuilt-businessCard</option>
                <option value="prebuilt-layout">prebuilt-layout</option>
              </select>
            </div>
          </>
        );
      case 'google_document_ai':
        return (
          <>
            {credentialPicker('GCP Service Account Credential')}
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Processor Resource Name</div>
              <input value={extractor.gcp_processor_name || ''} onChange={(e) => updateExtractor({ gcp_processor_name: e.target.value })}
                     placeholder="projects/{project}/locations/{location}/processors/{id}"
                     style={{ ...inputStyle, width: '100%', fontFamily: 'monospace', fontSize: 11 }} />
            </div>
          </>
        );
      case 'mistral_ocr':
        return credentialPicker('Mistral API Credential', 'Mistral OCR provides dense text — field extraction is delegated to LLM Vision.');
      default:
        return null;
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
        borderRadius: 8, padding: 20, width: 480, maxHeight: '85vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Configure: {docType.name}</div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={btnGhost}>×</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
          key: <span style={{ fontFamily: 'monospace' }}>{docType.key}</span> · family: {docType.family} · v{docType.version}
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Extraction Engine</div>
          <select value={engine} onChange={(e) => setEngine(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
            {ENGINE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {engineConfig()}

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
            Confidence Threshold: {threshold}%
          </div>
          <input type="range" min={50} max={99} value={threshold}
                 onChange={(e) => setThreshold(Number(e.target.value))} style={{ width: '100%' }} />
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
            Any field below this threshold routes the document to the review queue.
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
            Manual QC Sample: {qcPercentage}% {qcPercentage === 0 && <span style={{ color: 'var(--text-tertiary)' }}>— disabled</span>}
          </div>
          <input type="range" min={0} max={100} value={qcPercentage}
                 onChange={(e) => setQcPercentage(Number(e.target.value))} style={{ width: '100%' }} />
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
            Random sample of auto-extracted docs sent to manual QC in the xavvy Client Docuforge inbox. Reviewed docs are never re-sampled.
          </div>
        </div>

        <div style={{ marginTop: 14, padding: 10, border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--bg-primary)' }}>
          <div style={{ fontSize: 12, fontWeight: 600 }}>✍️ Handwriting Routing <span style={{ fontSize: 10, color: 'var(--text-tertiary)', marginLeft: 6 }}>Enterprise</span></div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
            When set, low-text-layer docs are auto-routed to Google Document AI (handwriting-enabled). Force always routes; Off keeps the engine above.
          </div>
          <select
            value={(extractor.handwriting_mode || 'off')}
            onChange={(e) => updateExtractor({ handwriting_mode: e.target.value })}
            style={{ ...inputStyle, width: '100%', marginTop: 8 }}
          >
            <option value="off">Off</option>
            <option value="auto">Auto (low text-layer triggers handwriting path)</option>
            <option value="force">Force (always route to Document AI)</option>
          </select>
        </div>

        {engine === 'llm_vision' && (
          <FewshotManager workspaceId={workspaceId} docTypeId={docType.id} setError={setError} />
        )}

        <FineTunePanel workspaceId={workspaceId} docTypeId={docType.id} setError={setError} />

        <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button disabled={saving} onClick={save} style={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}


function FewshotManager({ workspaceId, docTypeId, setError }) {
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [exName, setExName] = useState('');
  const [exSnippet, setExSnippet] = useState('');
  const [exPayload, setExPayload] = useState('{}');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/doc-types/${docTypeId}/fewshot-examples`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((d) => setExamples(d.examples || []))
      .catch((e) => setError(`Failed to load fewshots: ${e.statusText || e.message || e}`))
      .finally(() => setLoading(false));
  }, [workspaceId, docTypeId, setError]);

  useEffect(() => { load(); }, [load]);

  const addExample = () => {
    let parsed;
    try {
      parsed = JSON.parse(exPayload);
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) throw new Error('payload must be an object');
    } catch (e) {
      setError(`Invalid payload JSON: ${e.message}`);
      return;
    }
    if (!exSnippet.trim()) {
      setError('text_snippet is required');
      return;
    }
    setSaving(true);
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/doc-types/${docTypeId}/fewshot-examples`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: exName, text_snippet: exSnippet, payload: parsed }),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(() => {
        setExName(''); setExSnippet(''); setExPayload('{}'); setShowAdd(false);
        load();
      })
      .catch((e) => setError(`Add example failed: ${e.statusText || e.message || e}`))
      .finally(() => setSaving(false));
  };

  const deleteExample = (id) => {
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/doc-types/${docTypeId}/fewshot-examples/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(load)
      .catch((e) => setError(`Delete failed: ${e.statusText || e.message || e}`));
  };

  return (
    <div style={{
      marginTop: 14, padding: 10, border: '1px solid var(--border-color)', borderRadius: 6,
      background: 'var(--bg-primary)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ fontSize: 12, fontWeight: 600 }}>
          Fewshot Examples {!loading && <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>· {examples.length}</span>}
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowAdd(!showAdd)} style={btnGhost}>{showAdd ? 'Cancel' : '+ Add'}</button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
        Labeled examples lift LLM Vision accuracy and improve classification. Up to 20 stored; first 5 are injected into every extraction prompt.
      </div>
      {showAdd && (
        <div style={{ marginTop: 10 }}>
          <input value={exName} onChange={(e) => setExName(e.target.value)}
                 placeholder="Example name (optional)" style={{ ...inputStyle, width: '100%', marginBottom: 6 }} />
          <textarea value={exSnippet} onChange={(e) => setExSnippet(e.target.value)}
                    placeholder="Paste a representative text snippet from this doc type (used for classification + extractor prompt)"
                    style={{ ...inputStyle, width: '100%', minHeight: 60, fontSize: 11, marginBottom: 6 }} />
          <textarea value={exPayload} onChange={(e) => setExPayload(e.target.value)}
                    placeholder='Corrected payload JSON, e.g. {"vendor_name":"Acme","invoice_total":"1200.00"}'
                    style={{ ...inputStyle, width: '100%', minHeight: 80, fontFamily: 'monospace', fontSize: 11 }} />
          <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
            <button disabled={saving} onClick={addExample} style={btnPrimary}>{saving ? 'Saving…' : 'Save Example'}</button>
          </div>
        </div>
      )}
      {!loading && examples.length > 0 && (
        <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
          {examples.map((ex) => (
            <div key={ex.id} style={{
              display: 'flex', alignItems: 'center', padding: 6,
              background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 4,
            }}>
              <div style={{ flex: 1, fontSize: 11 }}>
                <div style={{ fontWeight: 600 }}>{ex.name}</div>
                <div style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>
                  {ex.text_snippet}
                </div>
              </div>
              <button onClick={() => deleteExample(ex.id)} style={btnGhost} title="Remove">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Subscribes to the workspace status websocket and fires `onChange` (debounced
// 400 ms) every time the pipeline publishes a `docuforge.status` event. Without
// this, the Documents tab and Review Queue tab only refresh on mount or on
// manual ↻ Refresh, so users had to bounce between tabs to see a doc move from
// `extracting` → `review_required` or `extracted`. Heartbeats are ignored. The
// socket auto-reconnects after 3 s on close/error.
function useDocuforgeStream(workspaceId, onChange) {
  const handlerRef = useRef(onChange);
  useEffect(() => { handlerRef.current = onChange; }, [onChange]);

  useEffect(() => {
    if (!workspaceId) return undefined;
    let ws;
    let reconnectTimer;
    let debounceTimer;
    let isMounted = true;

    const schedule = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (isMounted && handlerRef.current) handlerRef.current();
      }, 400);
    };

    const connect = () => {
      try {
        ws = new WebSocket(`${WS_BASE_URL}/api/docuforge/ws/workspaces/${workspaceId}`);
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data && data.type === 'docuforge.status') schedule();
          } catch (_) { /* swallow malformed frames */ }
        };
        ws.onclose = () => {
          if (isMounted) reconnectTimer = setTimeout(connect, 3000);
        };
        ws.onerror = () => { try { ws.close(); } catch (_) { /* noop */ } };
      } catch (_) {
        if (isMounted) reconnectTimer = setTimeout(connect, 3000);
      }
    };

    connect();

    return () => {
      isMounted = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (debounceTimer) clearTimeout(debounceTimer);
      if (ws) { try { ws.close(); } catch (_) { /* noop */ } }
    };
  }, [workspaceId]);
}

// ---------------------------------------------------------------------------
// Documents tab
// ---------------------------------------------------------------------------

function DocumentsTab({ workspaceId, setError }) {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    const q = filter ? `?status=${encodeURIComponent(filter)}&limit=100` : '?limit=100';
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/documents${q}`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((data) => { setDocs(data.documents || []); setTotal(data.total || 0); })
      .catch((e) => setError(`Failed to load documents: ${e.statusText || e.message || e}`))
      .finally(() => setLoading(false));
  }, [workspaceId, filter, setError]);

  useEffect(() => { load(); }, [load]);
  useDocuforgeStream(workspaceId, load);

  const upload = async (docTypeKey) => {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setUploading(true);
    const form = new FormData();
    form.append('file', f);
    const path = docTypeKey
      ? `/api/docuforge/workspaces/${workspaceId}/ingest/${docTypeKey}`
      : `/api/docuforge/workspaces/${workspaceId}/ingest`;
    try {
      const r = await fetch(`${API_BASE_URL}${path}?sync=true`, {
        method: 'POST',
        headers: authHeaders(),
        body: form,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail));
      }
      await r.json();
      fileRef.current.value = '';
      load();
    } catch (e) {
      setError(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  if (verifying) {
    return (
      <Verifier
        workspaceId={workspaceId}
        docId={verifying}
        onClose={() => { setVerifying(null); load(); }}
        setError={setError}
      />
    );
  }

  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} style={selectStyle}>
            <option value="">All statuses</option>
            <option value="queued">Queued</option>
            <option value="extracting">Extracting</option>
            <option value="review_required">Review required</option>
            <option value="extracted">Extracted</option>
            <option value="failed">Failed</option>
            <option value="discarded">Discarded</option>
          </select>
          <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{total} document(s)</div>
          <div style={{ flex: 1 }} />
          <input ref={fileRef} type="file" style={{ color: 'var(--text-secondary)' }} />
          <button disabled={uploading} onClick={() => upload(null)} style={btnPrimary}>Upload (auto-classify)</button>
          <button disabled={uploading} onClick={() => upload('invoice')} style={btnGhost}>Upload as Invoice</button>
        </div>
        {loading ? (
          <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>
        ) : (
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-tertiary)' }}>
                  <th style={thStyle}>Filename</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Doc Type</th>
                  <th style={thStyle}>Pages</th>
                  <th style={thStyle}>Min Conf</th>
                  <th style={thStyle}>Received</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} onClick={() => setSelected(d.id)} style={{ cursor: 'pointer', borderTop: '1px solid var(--border-color)' }}>
                    <td style={tdStyle}>{d.filename || '—'}</td>
                    <td style={tdStyle}>{statusBadge(d.status)}</td>
                    <td style={tdStyle}>{d.doc_type_name || d.doc_type_key || '—'}</td>
                    <td style={tdStyle}>{d.page_count ?? '—'}</td>
                    <td style={tdStyle}>{d.confidence_min != null ? confidencePill(d.confidence_min) : '—'}</td>
                    <td style={tdStyle}>{d.ts_received ? new Date(d.ts_received).toLocaleString() : '—'}</td>
                  </tr>
                ))}
                {!docs.length && (
                  <tr><td colSpan={6} style={{ ...tdStyle, color: 'var(--text-secondary)', textAlign: 'center' }}>No documents yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {selected && (
        <div style={{ width: 480, flexShrink: 0 }}>
          <DocumentDetail
            workspaceId={workspaceId}
            docId={selected}
            onClose={() => setSelected(null)}
            onAction={load}
            onEdit={(id) => setVerifying(id)}
            setError={setError}
          />
        </div>
      )}
    </div>
  );
}

function DocumentDetail({ workspaceId, docId, onClose, onAction, onEdit, setError }) {
  const [doc, setDoc] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [pickedDocType, setPickedDocType] = useState('');
  const load = useCallback(() => {
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/documents/${docId}`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setDoc)
      .catch((e) => setError(`Load failed: ${describeFetchError(e)}`));
  }, [workspaceId, docId, setError]);
  useEffect(() => { load(); }, [load]);

  // Pull the workspace's enabled doc types once — used by the manual assign
  // picker that escapes the "Re-classify reruns the same heuristic" trap when
  // auto-classify failed or picked the wrong type.
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/doc-types`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((data) => setDocTypes((data.doc_types || []).filter((dt) => dt.is_enabled)))
      .catch(() => { /* non-fatal: the picker just won't render */ });
  }, [workspaceId]);

  const act = (action) => {
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/documents/${docId}/${action}`, {
      method: 'POST', headers: authHeaders(),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(() => { load(); onAction && onAction(); })
      .catch((e) => setError(`${action} failed: ${describeFetchError(e)}`));
  };

  const assignDocType = () => {
    if (!pickedDocType) return;
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/documents/${docId}/assign-doc-type`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ doc_type_id: pickedDocType }),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(() => { setPickedDocType(''); load(); onAction && onAction(); })
      .catch((e) => setError(`Assign doc type failed: ${describeFetchError(e)}`));
  };

  if (!doc) return <div style={cardStyle}>Loading…</div>;
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ fontWeight: 700 }}>{doc.filename}</div>
        <div style={{ flex: 1 }} />
        <button onClick={onClose} style={btnGhost}>×</button>
      </div>
      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
        {statusBadge(doc.status)} · {doc.page_count ?? '?'} page(s) · {doc.mime_type}
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-secondary)' }}>
        sha256: <span style={{ fontFamily: 'monospace' }}>{(doc.sha256 || '').slice(0, 16)}…</span>
      </div>
      <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button
          onClick={async () => {
            try {
              const r = await fetch(
                `${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/documents/${docId}/file`,
                { headers: authHeaders() },
              );
              if (!r.ok) {
                const detail = await r.text().catch(() => '');
                throw new Error(`HTTP ${r.status} ${r.statusText || ''} ${detail.slice(0, 200)}`.trim());
              }
              const blob = await r.blob();
              const url = URL.createObjectURL(blob);
              const win = window.open(url, '_blank', 'noreferrer');
              // Revoke after a delay so the new tab has time to load.
              setTimeout(() => URL.revokeObjectURL(url), 60_000);
              if (!win) setError('Pop-up blocked — allow pop-ups to open the file.');
            } catch (e) {
              setError(`Open file failed: ${e.message || e}`);
            }
          }}
          style={btnGhost}
        >
          Open File
        </button>
        {onEdit && (doc.status === 'extracted' || doc.status === 'review_required') && (doc.fields || []).length > 0 && (
          <button onClick={() => onEdit(docId)} style={btnPrimary} title="Open the full editor to tweak any extracted value">
            ✎ Edit fields
          </button>
        )}
        <button onClick={() => act('reclassify')} style={btnGhost}>Re-classify</button>
        <button onClick={() => act('reextract')} style={btnGhost}>Re-extract</button>
        {doc.mime_type === 'application/pdf' && (doc.page_count || 0) > 1 && (
          <button onClick={() => act('split')} style={btnGhost} title="Detect document boundaries and split this multi-doc PDF">
            ✂ Split
          </button>
        )}
        <button onClick={() => act('discard')} style={btnDanger}>Discard</button>
      </div>
      {docTypes.length > 0 && (
        <div style={{ marginTop: 10, padding: 8, background: 'var(--bg-tertiary)', borderRadius: 4 }}>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
            {doc.error_class === 'unclassified'
              ? 'Auto-classify could not match a doc type. Pick one manually:'
              : 'Override doc type and re-extract:'}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <select
              value={pickedDocType}
              onChange={(e) => setPickedDocType(e.target.value)}
              style={{ ...inputStyle, flex: 1, fontSize: 12 }}
            >
              <option value="">— select doc type —</option>
              {docTypes.map((dt) => (
                <option key={dt.id} value={dt.id}>{dt.label || dt.key}</option>
              ))}
            </select>
            <button disabled={!pickedDocType} onClick={assignDocType} style={btnPrimary}>
              Assign &amp; Re-extract
            </button>
          </div>
          {doc.error_class === 'classification_error' && doc.error_detail && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-error, #f87171)' }}>
              Classifier crashed: <code>{doc.error_detail.slice(0, 200)}</code>
            </div>
          )}
        </div>
      )}
      {doc.fields && doc.fields.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Extracted Fields</div>
          <div style={{ background: 'var(--bg-tertiary)', borderRadius: 4, maxHeight: 360, overflow: 'auto' }}>
            {doc.fields.map((f, i) => (
              <div key={i} style={{ display: 'flex', padding: '6px 8px', borderBottom: '1px solid var(--border-color)', gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1, fontSize: 12, fontFamily: 'monospace' }}>{f.field_path}</div>
                <div style={{ flex: 2, fontSize: 12, wordBreak: 'break-word' }}>
                  {f.corrected_value ?? f.normalized_value ?? f.raw_value ?? <em>(empty)</em>}
                </div>
                <div style={{ flexShrink: 0 }}>{confidencePill(f.confidence)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review Queue tab
// ---------------------------------------------------------------------------

function ReviewQueueTab({ workspaceId, setError }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/review/queue?limit=100`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((data) => setDocs(data.documents || []))
      .catch((e) => setError(`Failed to load review queue: ${e.statusText || e}`))
      .finally(() => setLoading(false));
  }, [workspaceId, setError]);

  useEffect(() => { load(); }, [load]);
  useDocuforgeStream(workspaceId, load);

  if (selected) {
    return <Verifier workspaceId={workspaceId} docId={selected} onClose={() => { setSelected(null); load(); }} setError={setError} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Documents awaiting human verification</div>
        <div style={{ flex: 1 }} />
        <button onClick={load} style={btnGhost}>↻ Refresh</button>
      </div>
      {loading ? (
        <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>
      ) : !docs.length ? (
        <div style={{ color: 'var(--text-secondary)' }}>No documents in the review queue. 🎉</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {docs.map((d) => (
            <div key={d.id} onClick={() => setSelected(d.id)} style={{ ...cardStyle, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontWeight: 600 }}>{d.filename || d.id}</div>
                <div style={{ flex: 1 }} />
                {statusBadge(d.status)}
                {d.confidence_min != null && confidencePill(d.confidence_min)}
              </div>
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
                {d.doc_type_name || d.doc_type_key || 'unknown type'} · {d.page_count ?? '?'} page(s) ·
                {' '}received {d.ts_received ? new Date(d.ts_received).toLocaleString() : '—'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Money-typed fields are stored as ``{"amount":"…","currency":"…"}`` JSON
// strings. A plain text input shows raw JSON to the reviewer and silently
// destroys the structure when they type a number. ``FieldEditor`` parses the
// value: a money payload gets a split editor (amount + 3-letter ISO code);
// everything else falls back to a single text input.
const _CURRENCY_CODES = [
  'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'NZD', 'INR', 'JPY', 'CNY', 'CHF',
  'KRW', 'HKD', 'SGD', 'MXN', 'BRL', 'ZAR', 'SEK', 'NOK', 'DKK', 'PLN',
  'AED', 'TRY', 'ILS', 'RUB', 'THB',
];

function parseMoney(v) {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!s.startsWith('{') || !s.endsWith('}')) return null;
  try {
    const obj = JSON.parse(s);
    if (obj && typeof obj === 'object' && 'amount' in obj && 'currency' in obj) {
      return { amount: String(obj.amount ?? ''), currency: String(obj.currency ?? '').toUpperCase() };
    }
  } catch (_) { /* not JSON — fall through */ }
  return null;
}

function FieldEditor({ value, onChange }) {
  const money = parseMoney(value);
  if (!money) {
    return (
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
      />
    );
  }
  const update = (next) => onChange(JSON.stringify(next));
  const codes = _CURRENCY_CODES.includes(money.currency)
    ? _CURRENCY_CODES
    : [money.currency, ..._CURRENCY_CODES];
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input
        type="text"
        inputMode="decimal"
        value={money.amount}
        onChange={(e) => update({ amount: e.target.value, currency: money.currency })}
        style={{ ...inputStyle, flex: 1 }}
        placeholder="Amount"
      />
      <select
        value={money.currency}
        onChange={(e) => update({ amount: money.amount, currency: e.target.value })}
        style={{ ...inputStyle, width: 90 }}
      >
        {codes.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
  );
}

function Verifier({ workspaceId, docId, onClose, setError }) {
  const [doc, setDoc] = useState(null);
  const [edits, setEdits] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/documents/${docId}`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((d) => {
        setDoc(d);
        const initial = {};
        for (const f of d.fields || []) {
          initial[f.field_path] = f.corrected_value ?? f.normalized_value ?? f.raw_value ?? '';
        }
        setEdits(initial);
      })
      .catch((e) => setError(`Load failed: ${describeFetchError(e)}`));
  }, [workspaceId, docId, setError]);

  // Iframes can't carry bearer auth headers, so fetch the file as a blob and
  // hand the iframe an object URL instead.
  useEffect(() => {
    let revoked = false;
    let createdUrl = null;
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/documents/${docId}/file`, { headers: authHeaders() })
      .then((r) => r.ok ? r.blob() : Promise.reject(r))
      .then((blob) => {
        if (revoked) return;
        createdUrl = URL.createObjectURL(blob);
        setFileUrl(createdUrl);
      })
      .catch((e) => setError(`File load failed: ${describeFetchError(e)}`));
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      setFileUrl(null);
    };
  }, [workspaceId, docId, setError]);

  const submit = async (submitFinal) => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/documents/${docId}/review`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ corrections: edits, submit: submitFinal }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail));
      }
      onClose();
    } catch (e) {
      setError(`Review submit failed: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!doc) return <div style={{ color: 'var(--text-secondary)' }}>Loading document…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <button onClick={onClose} style={btnGhost}>← Back</button>
        <div style={{ marginLeft: 12, fontWeight: 700 }}>{doc.filename}</div>
        <div style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
          {statusBadge(doc.status)} · {doc.fields?.length || 0} fields · min conf
        </div>
        {doc.confidence_min != null && <div style={{ marginLeft: 6 }}>{confidencePill(doc.confidence_min)}</div>}
      </div>
      <div style={{ flex: 1, display: 'flex', gap: 12, minHeight: 0 }}>
        <div style={{ flex: 1, background: '#000', borderRadius: 6, overflow: 'hidden' }}>
          {fileUrl ? (
            <iframe
              title="document"
              src={fileUrl}
              style={{ width: '100%', height: '100%', border: 0 }}
            />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: 12 }}>
              Loading document…
            </div>
          )}
        </div>
        <div style={{ width: 460, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ ...cardStyle, flex: 1, overflow: 'auto' }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Extracted Fields</div>
            {(doc.fields || []).map((f) => (
              <div key={f.field_path} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-secondary)', flex: 1 }}>{f.field_path}</div>
                  {confidencePill(f.confidence)}
                </div>
                <FieldEditor
                  value={edits[f.field_path] ?? ''}
                  onChange={(v) => setEdits({ ...edits, [f.field_path]: v })}
                />
                {f.raw_value && f.raw_value !== edits[f.field_path] && (
                  <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>
                    Raw: <code>{f.raw_value.slice(0, 80)}</code>
                  </div>
                )}
              </div>
            ))}
            {!(doc.fields || []).length && (
              <div style={{ color: 'var(--text-secondary)' }}>No fields were extracted — add corrections manually if needed.</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, padding: '8px 0' }}>
            <button disabled={submitting} onClick={() => submit(false)} style={btnGhost}>Save Draft</button>
            <div style={{ flex: 1 }} />
            <button disabled={submitting} onClick={() => submit(true)} style={btnPrimary}>Submit ▶</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// P5 — Sources tab (email + watched folders)
// ---------------------------------------------------------------------------

const SOURCE_KIND_LABELS = {
  email_imap:  'Email — IMAP',
  email_graph: 'Email — Microsoft Graph',
  email_gmail: 'Email — Gmail',
  s3:          'S3 bucket',
  sharepoint:  'SharePoint drive',
  gdrive:      'Google Drive folder',
  dropbox:     'Dropbox folder',
};

const SOURCE_CONFIG_HINTS = {
  email_imap:  '{"folder":"INBOX","use_ssl":true,"max_emails":25}',
  email_graph: '{"folder":"Inbox","max_messages":25}',
  email_gmail: '{"label":"INBOX","max_messages":25}',
  s3:          '{"bucket":"my-bucket","prefix":"incoming/","max_objects":50}',
  sharepoint:  '{"drive_id":"b!...","folder_path":"/Shared Documents/Inbox","max_items":50}',
  gdrive:      '{"folder_id":"1AbCdEf...","max_files":50}',
  dropbox:     '{"folder":"/incoming","max_files":50}',
};

function SourcesTab({ workspaceId, setError }) {
  const [sources, setSources] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [pollingId, setPollingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/sources`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((data) => { setSources(data.sources || []); setKinds(data.kinds || []); })
      .catch((e) => setError(`Failed to load sources: ${e.statusText || e.message || e}`))
      .finally(() => setLoading(false));
  }, [workspaceId, setError]);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = (src) => {
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/sources/${src.id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_enabled: !src.is_enabled }),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(load)
      .catch((e) => setError(`Update failed: ${e.statusText || e}`));
  };

  const deleteSource = (src) => {
    if (!window.confirm(`Delete source "${src.name}"?`)) return;
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/sources/${src.id}`, {
      method: 'DELETE', headers: authHeaders(),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(load)
      .catch((e) => setError(`Delete failed: ${e.statusText || e}`));
  };

  const pollNow = (src) => {
    setPollingId(src.id);
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/sources/${src.id}/poll`, {
      method: 'POST', headers: authHeaders(),
    })
      .then((r) => r.ok ? r.json() : r.json().then((d) => Promise.reject(d)))
      .then((data) => {
        const msg = data.error
          ? `Poll error: ${data.error}`
          : `Polled ${data.fetched} item(s), ingested ${data.ingested}.`;
        if (data.error) setError(msg);
        else window.alert(msg);
        load();
      })
      .catch((e) => setError(`Poll failed: ${e.detail || e.statusText || JSON.stringify(e)}`))
      .finally(() => setPollingId(null));
  };

  if (loading) return <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>;
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Pull documents from email inboxes or watched cloud folders. Each source remembers what it has
          already ingested (sha256 dedupe + per-source cursor).
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(true)} style={btnPrimary}>+ New Source</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
        {sources.map((s) => (
          <div key={s.id} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{s.name}</div>
              <div style={{ flex: 1 }} />
              <span style={{
                background: '#5d4eaa', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 3,
              }}>{SOURCE_KIND_LABELS[s.kind] || s.kind}</span>
            </div>
            {s.doc_type_key && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                Pinned to doc type: <b>{s.doc_type_key}</b>
              </div>
            )}
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {JSON.stringify(s.config || {})}
            </div>
            <div style={{ marginTop: 8, fontSize: 11 }}>
              Last poll:{' '}
              {s.last_poll_at ? (
                <>
                  <span style={{
                    background: s.last_poll_status === 'error' ? '#c0392b'
                              : s.last_poll_status === 'empty' ? '#666' : '#1e8449',
                    color: '#fff', padding: '1px 6px', borderRadius: 3, fontSize: 10,
                  }}>{s.last_poll_status}</span>
                  {' '}<span style={{ color: 'var(--text-secondary)' }}>{new Date(s.last_poll_at).toLocaleString()}</span>
                  {' · '}{s.last_poll_count || 0} ingested
                </>
              ) : (
                <span style={{ color: 'var(--text-secondary)' }}>never</span>
              )}
            </div>
            {s.last_poll_detail && (
              <div style={{ marginTop: 4, fontSize: 11, color: '#ffb4b4' }}>
                {s.last_poll_detail}
              </div>
            )}
            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => toggleEnabled(s)} style={s.is_enabled ? btnSuccess : btnGhost}>
                {s.is_enabled ? '✓ Enabled' : 'Disabled'}
              </button>
              <button
                onClick={() => pollNow(s)}
                disabled={!s.is_enabled || pollingId === s.id}
                style={btnGhost}
              >
                {pollingId === s.id ? 'Polling…' : '⟳ Poll now'}
              </button>
              <button onClick={() => deleteSource(s)} style={btnDanger}>Delete</button>
            </div>
          </div>
        ))}
        {!sources.length && (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            No sources configured. Click <b>+ New Source</b> to wire an email inbox or watched folder.
          </div>
        )}
      </div>
      {creating && (
        <NewSourceModal
          workspaceId={workspaceId}
          kinds={kinds.length ? kinds : Object.keys(SOURCE_KIND_LABELS)}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
          setError={setError}
        />
      )}
    </>
  );
}


function NewSourceModal({ workspaceId, kinds, onClose, onCreated, setError }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState(kinds[0] || 'email_imap');
  const [docTypeKey, setDocTypeKey] = useState('');
  const [credentialId, setCredentialId] = useState('');
  const [configText, setConfigText] = useState(SOURCE_CONFIG_HINTS[kinds[0] || 'email_imap']);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setConfigText(SOURCE_CONFIG_HINTS[kind] || '{}');
  }, [kind]);

  const save = () => {
    let config = {};
    try {
      config = configText.trim() ? JSON.parse(configText) : {};
    } catch (e) {
      setError(`Invalid JSON in config: ${e.message}`);
      return;
    }
    setSaving(true);
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/sources`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name, kind,
        doc_type_key: docTypeKey || null,
        credential_id: credentialId || null,
        config,
        is_enabled: true,
      }),
    })
      .then((r) => r.ok ? r.json() : r.json().then((d) => Promise.reject(d)))
      .then(onCreated)
      .catch((e) => setError(`Create failed: ${e.detail || e.message || JSON.stringify(e)}`))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{ ...cardStyle, width: 560, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>New Source</div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={btnGhost}>×</button>
        </div>
        <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
          <label style={{ fontSize: 12 }}>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 12 }}>
            Kind
            <select value={kind} onChange={(e) => setKind(e.target.value)} style={{ ...inputStyle, marginTop: 4 }}>
              {kinds.map((k) => (
                <option key={k} value={k}>{SOURCE_KIND_LABELS[k] || k}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12 }}>
            Doc type key (optional pin — leave blank to auto-classify)
            <input value={docTypeKey} onChange={(e) => setDocTypeKey(e.target.value)} placeholder="invoice" style={{ ...inputStyle, marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 12 }}>
            Credential ID (workspace credential storing the connection secrets)
            <input value={credentialId} onChange={(e) => setCredentialId(e.target.value)} placeholder="cred_..." style={{ ...inputStyle, marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 12 }}>
            Config JSON
            <textarea
              value={configText}
              onChange={(e) => setConfigText(e.target.value)}
              rows={6}
              style={{ ...inputStyle, marginTop: 4, fontFamily: 'monospace', fontSize: 12 }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
              Hint for <b>{kind}</b>: <code>{SOURCE_CONFIG_HINTS[kind]}</code>
            </div>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <div style={{ flex: 1 }} />
          <button disabled={!name || saving} onClick={save} style={btnPrimary}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// styles
// ---------------------------------------------------------------------------

const btnGhost = {
  background: 'transparent',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: 4,
  padding: '4px 10px',
  fontSize: 12,
  cursor: 'pointer',
};
const btnPrimary = { ...btnGhost, background: 'var(--accent-color)', color: '#fff', borderColor: 'transparent' };
const btnSuccess = { ...btnGhost, background: '#1e8449', color: '#fff', borderColor: 'transparent' };
const btnDanger = { ...btnGhost, background: '#c0392b', color: '#fff', borderColor: 'transparent' };
const cardStyle = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: 6,
  padding: 12,
};
const thStyle = { textAlign: 'left', padding: '8px', fontWeight: 600, fontSize: 12, color: 'var(--text-secondary)' };
const tdStyle = { padding: '8px', verticalAlign: 'middle' };
const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  background: 'var(--bg-tertiary)',
  border: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
  borderRadius: 4,
  padding: '6px 8px',
  fontSize: 13,
};
const selectStyle = { ...inputStyle, width: 'auto', padding: '4px 8px' };

function tabPillStyle(active) {
  return {
    background: active ? 'var(--accent-color)' : 'transparent',
    color: active ? '#fff' : 'var(--text-primary)',
    border: '1px solid ' + (active ? 'transparent' : 'var(--border-color)'),
    borderRadius: 999,
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  };
}

// ---------------------------------------------------------------------------
// Reports tab — Phase 6 accuracy dashboard (DOCUFORGE_PRD §7.4)
// ---------------------------------------------------------------------------

function ReportsTab({ workspaceId, setError, licenseAllowed }) {
  const [days, setDays] = useState(30);
  const [grid, setGrid] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [cost, setCost] = useState(null);
  const [qc, setQc] = useState(null);
  const [bucket, setBucket] = useState('day');
  const [groupBy, setGroupBy] = useState('engine');
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    if (!licenseAllowed) { setLoading(false); return; }
    const end = new Date();
    const start = new Date(end.getTime() - days * 86400000);
    const qs = `start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`;
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/widgets/accuracy_grid?${qs}`, { headers: authHeaders() }).then((r) => r.ok ? r.json() : Promise.reject(r)),
      fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/widgets/throughput_funnel?${qs}`, { headers: authHeaders() }).then((r) => r.ok ? r.json() : Promise.reject(r)),
      fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/widgets/cost_breakdown?${qs}&bucket=${bucket}&group_by=${groupBy}`, { headers: authHeaders() }).then((r) => r.ok ? r.json() : Promise.reject(r)),
      fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/widgets/qc_outcomes?${qs}`, { headers: authHeaders() }).then((r) => r.ok ? r.json() : Promise.reject(r)),
    ])
      .then(([g, f, c, q]) => { setGrid(g); setFunnel(f); setCost(c); setQc(q); setError(null); })
      .catch((e) => setError(describeFetchError(e)))
      .finally(() => setLoading(false));
  }, [workspaceId, days, bucket, groupBy, licenseAllowed, setError]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!licenseAllowed) {
    return (
      <div style={{ ...cardStyle, padding: 32, textAlign: 'center', maxWidth: 600, margin: '40px auto' }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Accuracy Dashboard requires Enterprise</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          Per-doc-type accuracy heatmap, throughput funnel, and cost breakdown widgets are
          part of the docuforge Enterprise plan. Contact xavvy support to upgrade.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 600 }}>Window</div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={selectStyle}>
          <option value={1}>Last 24h</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <div style={{ marginLeft: 16, fontWeight: 600 }}>Cost bucket</div>
        <select value={bucket} onChange={(e) => setBucket(e.target.value)} style={selectStyle}>
          <option value="hour">Hour</option>
          <option value="day">Day</option>
          <option value="week">Week</option>
          <option value="month">Month</option>
        </select>
        <div style={{ marginLeft: 16, fontWeight: 600 }}>Stack by</div>
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} style={selectStyle}>
          <option value="engine">Engine</option>
          <option value="doc_type">Doc Type</option>
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={refresh} style={btnGhost}>↻ Refresh</button>
      </div>

      {loading && <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>}

      <ThroughputFunnel data={funnel} />
      <QcOutcomes data={qc} />
      <AccuracyGrid data={grid} />
      <CostBreakdown data={cost} />
    </div>
  );
}

function QcOutcomes({ data }) {
  if (!data) return null;
  const rows = data.by_doc_type || [];
  const t = data.totals || {};
  if (!rows.length) {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Manual QC Outcomes</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
          No QC samples reviewed in window. Set <code>qc_percentage</code> &gt; 0 on a doc type to start sampling.
        </div>
      </div>
    );
  }
  const fmtPct = (v) => v == null ? '—' : `${Math.round(v * 100)}%`;
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Manual QC Outcomes</div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
        <div>Reviewed: <b style={{ color: 'var(--text-primary)' }}>{t.reviewed}</b></div>
        <div>Pass: <b style={{ color: '#16a085' }}>{t.pass}</b></div>
        <div>Fail: <b style={{ color: '#c0392b' }}>{t.fail}</b></div>
        <div>Pass rate: <b style={{ color: 'var(--text-primary)' }}>{fmtPct(t.pass_rate)}</b></div>
        <div>Corrections: <b style={{ color: 'var(--text-primary)' }}>{t.corrections}</b></div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--bg-tertiary)' }}>
              <th style={thStyle}>Doc type</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Configured %</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Reviewed</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Pass</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Fail</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Pass rate</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Sampled share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const drift = r.sampled_share != null && r.qc_percentage > 0
                ? Math.abs(r.sampled_share * 100 - r.qc_percentage)
                : null;
              const sampledColor = drift == null ? 'var(--text-primary)' : (drift > 10 ? '#d68910' : 'var(--text-primary)');
              return (
                <tr key={r.doc_type_id || '_unc'}>
                  <td style={tdStyle}>{r.doc_type_name}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{r.qc_percentage ? `${r.qc_percentage}%` : '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{r.reviewed}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#16a085', fontWeight: 600 }}>{r.pass}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#c0392b', fontWeight: 600 }}>{r.fail}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmtPct(r.pass_rate)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: sampledColor }}
                      title={drift != null && drift > 10 ? `Drift ${drift.toFixed(1)} pp vs configured ${r.qc_percentage}%` : ''}>
                    {fmtPct(r.sampled_share)}{drift != null && drift > 10 ? ' ⚠' : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ThroughputFunnel({ data }) {
  if (!data) return null;
  const max = Math.max(1, ...(data.stages || []).map((s) => s.count));
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Throughput Funnel</div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
        <div>STP rate: <b style={{ color: 'var(--text-primary)' }}>{data.kpis.stp_rate == null ? '—' : `${Math.round(data.kpis.stp_rate * 100)}%`}</b></div>
        <div>Review rate: <b style={{ color: 'var(--text-primary)' }}>{data.kpis.review_rate == null ? '—' : `${Math.round(data.kpis.review_rate * 100)}%`}</b></div>
        <div>Received: <b style={{ color: 'var(--text-primary)' }}>{data.kpis.received}</b></div>
        <div>Failed: <b style={{ color: '#c0392b' }}>{data.exceptions.failed}</b></div>
        <div>Discarded: <b style={{ color: '#7f8c8d' }}>{data.exceptions.discarded}</b></div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(data.stages || []).map((s) => {
          const pct = (s.count / max) * 100;
          return (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 110, fontSize: 12, fontWeight: 600 }}>{s.label}</div>
              <div style={{ flex: 1, height: 24, background: 'var(--bg-tertiary)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, var(--accent-color), #5dade2)',
                  display: 'flex', alignItems: 'center', paddingLeft: 8,
                  fontSize: 12, fontWeight: 600, color: '#fff',
                }}>{s.count}</div>
              </div>
              <div style={{ width: 100, textAlign: 'right', fontSize: 11, color: 'var(--text-secondary)' }}>
                {s.drop_from_prev == null ? '' : `−${s.drop_from_prev} drop`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AccuracyGrid({ data }) {
  if (!data) return null;
  const docTypes = data.doc_types || [];
  if (!docTypes.length) {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Accuracy Grid</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No reviewed documents in window.</div>
      </div>
    );
  }
  // Compute the union of all fields (rows) across doc types for the grid.
  const allFields = Array.from(new Set(
    docTypes.flatMap((dt) => data.fields_by_doc_type[dt.id] || [])
  )).sort();
  const cellMap = {};
  (data.cells || []).forEach((c) => { cellMap[`${c.doc_type_id}|${c.field_path}`] = c; });

  const colorFor = (acc) => {
    if (acc == null) return 'var(--bg-tertiary)';
    if (acc >= 0.95) return '#1e8449';
    if (acc >= 0.85) return '#16a085';
    if (acc >= 0.7) return '#d68910';
    return '#c0392b';
  };

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Accuracy Grid</div>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
        {data.totals.docs} docs · {data.totals.extractions} fields · {data.totals.corrections} corrections
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, position: 'sticky', left: 0, background: 'var(--bg-secondary)' }}>Field</th>
              {docTypes.map((dt) => (
                <th key={dt.id} style={{ ...thStyle, textAlign: 'center' }}>{dt.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allFields.map((fp) => (
              <tr key={fp}>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11, position: 'sticky', left: 0, background: 'var(--bg-secondary)' }}>{fp}</td>
                {docTypes.map((dt) => {
                  const c = cellMap[`${dt.id}|${fp}`];
                  if (!c) return <td key={dt.id} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-secondary)' }}>—</td>;
                  return (
                    <td key={dt.id} style={{ ...tdStyle, textAlign: 'center' }}>
                      <span style={{
                        background: colorFor(c.accuracy),
                        color: '#fff', padding: '2px 6px', borderRadius: 4,
                        fontWeight: 600, minWidth: 44, display: 'inline-block',
                      }} title={`${c.correct}/${c.total} correct · conf ${c.avg_confidence == null ? '—' : Math.round(c.avg_confidence * 100) + '%'}`}>
                        {c.accuracy == null ? '—' : `${Math.round(c.accuracy * 100)}%`}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CostBreakdown({ data }) {
  if (!data) return null;
  const buckets = data.buckets || [];
  const series = data.series || [];
  if (!buckets.length) {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Cost Breakdown</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No cost data in window.</div>
      </div>
    );
  }
  const palette = ['#3498db', '#e67e22', '#16a085', '#9b59b6', '#f1c40f', '#e74c3c', '#7f8c8d', '#1abc9c'];
  const totalsByBucket = buckets.map((_, i) => series.reduce((acc, s) => acc + (s.cost_usd[i] || 0), 0));
  const maxBucket = Math.max(1e-9, ...totalsByBucket);
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Cost Breakdown — {data.group_by} · {data.bucket}</div>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
        Total ${data.totals.cost_usd.toFixed(4)} · {data.totals.pages_billed} pages · {data.totals.docs} docs
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', height: 160, gap: 4, borderBottom: '1px solid var(--border-color)', paddingBottom: 4 }}>
        {buckets.map((b, i) => {
          const total = totalsByBucket[i];
          const h = (total / maxBucket) * 150;
          return (
            <div key={b} style={{ flex: 1, display: 'flex', flexDirection: 'column-reverse', height: '100%', position: 'relative' }} title={`${b}: $${total.toFixed(4)}`}>
              {series.map((s, sIdx) => {
                const v = s.cost_usd[i] || 0;
                if (!v) return null;
                const segH = (v / maxBucket) * 150;
                return (
                  <div key={s.name} style={{ height: segH, background: palette[sIdx % palette.length] }} />
                );
              })}
              {total > 0 && (
                <div style={{ position: 'absolute', bottom: h + 2, left: 0, right: 0, textAlign: 'center', fontSize: 9, color: 'var(--text-secondary)' }}>
                  ${total < 0.01 ? total.toFixed(4) : total.toFixed(2)}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
        {series.map((s, idx) => (
          <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
            <span style={{ width: 10, height: 10, background: palette[idx % palette.length], display: 'inline-block', borderRadius: 2 }} />
            {s.name}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 4, marginTop: 4, fontSize: 9, color: 'var(--text-secondary)' }}>
        {buckets.map((b) => (
          <div key={b} style={{ flex: 1, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b}</div>
        ))}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// P7 — Settings tab (Budget guardrail)
// ---------------------------------------------------------------------------

function SettingsTab({ workspaceId, setError }) {
  const [status, setStatus] = useState(null);
  const [limit, setLimit] = useState('');
  const [alertPct, setAlertPct] = useState('80');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/budget`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((d) => {
        setStatus(d);
        setLimit(String(d.daily_usd_limit || ''));
        setAlertPct(String(d.alert_at_pct ?? 80));
      })
      .catch((e) => setError(`Failed to load budget: ${e.statusText || e.message || e}`));
  }, [workspaceId, setError]);

  useEffect(() => { load(); }, [load]);

  const save = () => {
    setSaving(true);
    const body = {
      daily_usd_limit: limit === '' ? 0 : Number(limit),
      alert_at_pct: Number(alertPct),
    };
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/budget`, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setStatus)
      .catch((e) => setError(`Save failed: ${e.statusText || e.message || e}`))
      .finally(() => setSaving(false));
  };

  if (!status) return <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>;

  const pct = status.percent;
  const cap = Number(status.daily_usd_limit) > 0;
  const today = Number(status.today_spend || 0);
  const remaining = status.remaining;
  const barColor = !cap
    ? '#5a8dee'
    : status.would_block ? '#ef4444' : status.would_alert ? '#f59e0b' : '#10b981';
  const widthPct = !cap ? 0 : Math.min(100, Math.max(0, pct || 0));

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ ...cardStyle, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>💰 Daily Cost Guardrail</div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>per-workspace, rolling UTC day</div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
          Caps spend on paid extractor engines (Textract, Azure DI, Document AI, Mistral OCR, LLM Vision). When the cap is hit, the pipeline marks subsequent docs as <code>failed / budget_exceeded</code> until the next UTC day rollover. Tesseract is always free.
        </div>

        <div style={{ display: 'flex', gap: 14, marginTop: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Daily Limit (USD)</div>
            <input
              type="number" min="0" step="0.5" value={limit}
              onChange={(e) => setLimit(e.target.value)}
              placeholder="0 = unlimited"
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          <div style={{ width: 140 }}>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Alert At %</div>
            <input
              type="number" min="0" max="100" step="5" value={alertPct}
              onChange={(e) => setAlertPct(e.target.value)}
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <button disabled={saving} onClick={save} style={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: barColor }}>${today.toFixed(2)}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>spent today (UTC)</div>
            <div style={{ flex: 1 }} />
            {cap && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {remaining != null ? <>${Number(remaining).toFixed(2)} remaining of ${Number(status.daily_usd_limit).toFixed(2)} cap</> : null}
              </div>
            )}
          </div>
          <div style={{
            marginTop: 6, height: 8, borderRadius: 4, background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)', overflow: 'hidden',
          }}>
            <div style={{ width: `${widthPct}%`, height: '100%', background: barColor, transition: 'width 0.3s' }} />
          </div>
          {!cap && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>
              No cap configured — every paid engine call goes through. Set a daily limit above to protect the workspace from runaway LLM spend.
            </div>
          )}
          {cap && status.would_block && (
            <div style={{ fontSize: 11, color: '#ef4444', marginTop: 6 }}>
              🚫 Cap reached. Further paid extractions will fail with <code>budget_exceeded</code> until 00:00 UTC.
            </div>
          )}
          {cap && status.would_alert && !status.would_block && (
            <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 6 }}>
              ⚠ Past the alert threshold — workflows scheduled to ingest large PDFs may exhaust the cap.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// P7 — Fine-tune panel (embedded inside DocTypeConfigModal)
// ---------------------------------------------------------------------------

function FineTunePanel({ workspaceId, docTypeId, setError }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openaiSubmit, setOpenaiSubmit] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/doc-types/${docTypeId}/fine-tune`, {
      headers: authHeaders(),
    })
      .then((r) => {
        if (r.status === 402) { setLocked(true); return { jobs: [] }; }
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then((d) => setJobs(d.jobs || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [workspaceId, docTypeId]);

  useEffect(() => { load(); }, [load]);

  const submit = () => {
    setSubmitting(true);
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/doc-types/${docTypeId}/fine-tune`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ openai_finetune: openaiSubmit }),
    })
      .then((r) => r.ok ? r.json() : r.json().then((d) => Promise.reject(new Error(d.detail || r.statusText))))
      .then(() => load())
      .catch((e) => setError(`Fine-tune submit failed: ${e.message || e}`))
      .finally(() => setSubmitting(false));
  };

  const promote = (jobId) => {
    fetch(`${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/fine-tune/${jobId}/promote`, {
      method: 'POST',
      headers: authHeaders(),
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(() => load())
      .catch((e) => setError(`Promote failed: ${e.statusText || e.message || e}`));
  };

  if (locked) {
    return (
      <div style={{ marginTop: 14, padding: 10, border: '1px dashed var(--border-color)', borderRadius: 6, background: 'var(--bg-primary)' }}>
        <div style={{ fontSize: 12, fontWeight: 600 }}>🔒 Fine-Tuning <span style={{ fontSize: 10, color: 'var(--text-tertiary)', marginLeft: 6 }}>Enterprise</span></div>
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
          Upgrade to Enterprise to promote gold labels into the few-shot pool and submit engine-native custom-extractor training jobs.
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14, padding: 10, border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--bg-primary)' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ fontSize: 12, fontWeight: 600 }}>🧪 Fine-Tune <span style={{ fontSize: 10, color: 'var(--text-tertiary)', marginLeft: 6 }}>Enterprise</span></div>
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: 11, color: 'var(--text-secondary)', marginRight: 8 }}>
          <input type="checkbox" checked={openaiSubmit} onChange={(e) => setOpenaiSubmit(e.target.checked)} /> + OpenAI submit
        </label>
        <button disabled={submitting} onClick={submit} style={btnPrimary}>{submitting ? 'Submitting…' : 'Submit'}</button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
        Requires ≥10 gold labels. Few-shot promotion appends top examples to the prompt pool; engine-native paths export training JSONL for manual Textract / Azure DI / Document AI submission.
      </div>
      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {loading && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Loading…</div>}
        {!loading && !jobs.length && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>No fine-tune jobs yet.</div>}
        {jobs.map((j) => (
          <div key={j.id} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: 6,
            border: '1px solid var(--border-color)', borderRadius: 4, fontSize: 11,
          }}>
            <span style={{
              padding: '1px 5px', borderRadius: 3, color: '#fff', fontSize: 9,
              background: j.status === 'promoted' ? '#10b981'
                : j.status === 'candidate' ? '#5a8dee'
                : j.status === 'failed' ? '#ef4444' : '#666',
            }}>{j.status}</span>
            <span style={{ fontFamily: 'monospace' }}>{j.kind}</span>
            <span style={{ color: 'var(--text-secondary)' }}>{j.labels_used} labels</span>
            {j.before_accuracy != null && <span style={{ color: 'var(--text-secondary)' }}>baseline {Math.round(j.before_accuracy * 100)}%</span>}
            <span style={{ flex: 1 }} />
            {j.status === 'candidate' && (
              <button onClick={() => promote(j.id)} style={btnSuccess}>Promote</button>
            )}
            {j.error_detail && <span style={{ color: '#ef4444' }} title={j.error_detail}>error</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
